<script setup>
import { onMounted, ref } from 'vue'
import AdminLayout from '@/layouts/AdminLayout.vue'
import SubmitButton from '@/components/Forms/Buttons/SubmitButton.vue'
import EndpointTableCellMenu from '@/components/Tables/EndpointTableCellMenu.vue'
import { ButtonSize, ButtonType, ButtonWidth } from '@/components/Forms/Buttons/SubmitButtonConstants.js'
import TextEntryModal from '@/components/Alerts/TextEntryModal.vue'
import { documentsApi } from '@/api/Documents/DocumentsApi.js'
import { Toaster } from '@/models/utils/Toaster.js'
import { ModalButtonData } from '@/components/Alerts/ModalButtonData.js'
import MultiButtonModal from '@/components/Alerts/MultiButtonModal.vue'
import InputText from '@/components/Forms/InputText.vue'
import { userApi } from '@/api/User/UserApi.js'
import { useAuthStore } from '@/stores/authStore.js'
import LoadingSpinner from '@/components/Spinners/LoadingSpinner.vue'
import PortalDataTable from '@/components/DataTable/PortalDataTable.vue'
import { DataTableEndpointDefinition } from '@/models/datatable/DataTableEndpointDefinition.js'

const tagToEdit = ref(null)
const tagToDisable = ref(null)
const tagToEnable = ref(null)
const isTagSaving = ref(false)
const tenantSettings = ref(null)
const archiveAfterDays = ref(365)
const archiveAfterDaysDescription = ref(null)
const isSaving = ref(false)
const isLoading = ref(false)
const toaster = new Toaster()

const columns = [
	{ field: "name", title: "Name", sort: true, search: true, width: '100%' },
	{ field: "actions", title: "Actions", sort: false, search: false },
]

const endpoints = [
	new DataTableEndpointDefinition({
		endpointRoute: 'documents/tags',
		initialSortColumn: 'name',
		searchPlaceholder: 'Search by name',
		columns: columns,
	})
]

const table = ref(null)

onMounted(() => {
	isLoading.value = true

	userApi.getTenantById(useAuthStore().getAuth.user().tenant_id, (tenantData) => {
		tenantSettings.value = JSON.parse(tenantData.settings_json)
		archiveAfterDays.value = tenantSettings.value.documents_archive_after_days?.value ?? 365
		archiveAfterDaysDescription.value = tenantSettings.value.documents_archive_after_days?.description ??
			"Documents are considered \"archived\" after this many days:"

		isLoading.value = false
	})
});

function getMenuItems(tag)
{
	let items = [];

	if (tag.deleted_at) {
		items.push({ id: 1, text:'Enable' })
	} else {
		items.push({ id: 1, text:'Edit' })
		items.push({ id: 2, text:'Disable' })
	}

	return items;
}

function onMenuItemSelected(p_menu_item, p_record)
{
	switch (p_menu_item.text) {
		case "Edit":
			tagToEdit.value = p_record
			break;
		case "Disable":
			tagToDisable.value = p_record
			break;
		case "Enable":
			tagToEnable.value = p_record
			break;
	}
}

function onAddTagClicked() {
	tagToEdit.value = {}
}

function saveTag(newTagText) {
	isTagSaving.value = true

	tagToEdit.value.text = newTagText

	if (tagToEdit.value.id) {
		documentsApi.updateTag(
			tagToEdit.value,
			() => {
				tagToEdit.value = null
				isTagSaving.value = false
				toaster.success("Tag successfully updated.")
				table.value.loadData()
			},
			() => {
				tagToEdit.value = null
				isTagSaving.value = false
				toaster.error("Failed to update tag. Please try again.")
			}
		)
	}
	else {
		documentsApi.createTag(
			tagToEdit.value,
			() => {
				tagToEdit.value = null
				isTagSaving.value = false
				toaster.success("Tag successfully created.")
				table.value.loadData()
			},
			() => {
				tagToEdit.value = null
				isTagSaving.value = false
				toaster.error("Failed to create tag. Please try again.")
			}
		)
	}
}

function disableTag(tag) {
	isTagSaving.value = true

	documentsApi.disableTag(
		tag.id,
		() => {
			isTagSaving.value = false
			tagToDisable.value = null
			toaster.success("Tag successfully disabled.")
			table.value.loadData()
		},
		() => {
			isTagSaving.value = false
			tagToDisable.value = null
			toaster.error("Failed to disable tag. Please try again.")
		}
	)
}

function enableTag(tag) {
	isTagSaving.value = true

	documentsApi.enableTag(
		tag.id,
		() => {
			isTagSaving.value = false
			tagToEnable.value = null
			toaster.success("Tag successfully enabled.")
			table.value.loadData()
		},
		() => {
			isTagSaving.value = false
			tagToEnable.value = null
			toaster.error("Failed to enable tag. Please try again.")
		}
	)
}

function updateArchiveDays() {
	const settings = {
		'documents_archive_after_days': archiveAfterDays.value,
	}

	isSaving.value = true
	userApi.updateTenantSettings({ settings: settings },
		() => {
			isSaving.value = false
			toaster.success('Settings successfully updated!')

			userApi.refreshUserData()
		}, (e) => {
			toaster.error(e.showErrorBanner())
			isSaving.value = false
		})
}

</script>

<template>
	<AdminLayout>
		<p class="admin-center-subtitle">Documents Settings</p>

		<div v-if="!isLoading" class="flex mt-4 mb-6 space-x-4">
			<span class="h-fit my-auto">{{ archiveAfterDaysDescription }}</span>
			<InputText class="max-w-15.5 !mb-0" v-model="archiveAfterDays" :max-length="3" placeholder="" input-height="h-10" :disabled="isLoading"/>
			<SubmitButton label="Update" @click="updateArchiveDays" size="extra-small" :loading="isLoading || isSaving" :disabled="isLoading || isSaving"/>
		</div>
		<div v-else class="flex mt-4 mb-4 space-x-4">
			<LoadingSpinner class="!p-0" size="24"/>
			<span class="font-medium">Loading settings...</span>
		</div>

		<div class="flex flex-row mb-4">
			<p class="admin-center-subtitle">Document Tags</p>
			<SubmitButton label="Add Tag" @click="onAddTagClicked" size="small"/>
		</div>

		<PortalDataTable
			ref="table"
			:endpoints="endpoints"
		>
			<template #name="data">
				<span :class="[{ 'line-through': data.value.deleted_at }]">
					{{ data.value.name}}
				</span>
			</template>

			<template #actions="data">
				<div class="flex w-full justify-center">
					<EndpointTableCellMenu
						:items="getMenuItems(data.value)"
						:record="data.value"
						:disabled="false"
						@onMenuItemSelected="onMenuItemSelected"
					/>
				</div>
			</template>
		</PortalDataTable>

		<Teleport to="html">
			<TextEntryModal
				:show="tagToEdit != null"
				:twoButtonModal="true"
				title="Edit tag"
				:button-size="ButtonSize.Small"
				:button-width="ButtonWidth.Full"
				button-text="Submit"
				:default-text="tagToEdit?.name"
				:loading="isTagSaving"
				@onButtonClicked="saveTag"
				@dismiss="tagToEdit = null"
				:max-length="255"
				:allow-empty="false"
			/>
		</Teleport>

		<Teleport to="html">
			<MultiButtonModal
				:show="tagToDisable != null"
				icon="alert"
				title="Disable Tag"
				message="Are you sure you want to disable this tag?<br><br>If disabled, this tag will no longer appear on any documents that currently reference it."
				:buttons="[
										new ModalButtonData(
											'Cancel', () => { tagToDisable = null }, ButtonType.Default, ButtonSize.Small
										),
										new ModalButtonData(
											'Disable', () => { disableTag(tagToDisable) }, ButtonType.Default, ButtonSize.Small
										)
									]"
			/>
		</Teleport>

		<Teleport to="html">
			<MultiButtonModal
				:show="tagToEnable != null"
				icon="alert"
				title="Enable Tag"
				message="Are you sure you want to enable this tag?<br><br>If enabled, this tag may appear on any documents that referenced it before it was disabled."
				:buttons="[
										new ModalButtonData(
											'Cancel', () => { tagToEnable = null }, ButtonType.Default, ButtonSize.Small
										),
										new ModalButtonData(
											'Enable', () => { enableTag(tagToEnable) }, ButtonType.Default, ButtonSize.Small
										)
									]"
			/>
		</Teleport>
	</AdminLayout>
</template>